  /*============================================================================
    ToolTip
  ==============================================================================*/
  
  class ToolTip extends HTMLElement {
    constructor() {
      super();
      this.el = this;
      this.inner = this.querySelector('[data-tool-tip-inner]');
      this.closeButton = this.querySelector('[data-tool-tip-close]');
      this.toolTipContent = this.querySelector('[data-tool-tip-content]');
      this.toolTipTitle = this.querySelector('[data-tool-tip-title]');
  
      this.triggers = document.querySelectorAll('[data-tool-tip-trigger]');
  
      document.addEventListener('tooltip:open', e => {
        this._open(e.detail.context, e.detail.content);
      });
    }
  
    _open(context, insertedHtml) {
      this.toolTipContent.innerHTML = insertedHtml;
  
      // Ensure we set a title for product availability
      if (context != 'store-availability') {
        this.toolTipTitle.remove();
      }
  
      this._lockScrolling();
  
      if (this.closeButton) {
        this.closeButton.on('click' + '.tooltip-close', () => {
          this._close();
        });
      }
  
      document.documentElement.on('click' + '.tooltip-outerclick', event => {
        if (this.el.dataset.toolTipOpen === 'true' && !this.inner.contains(event.target)) this._close();
      });
  
      document.documentElement.on('keydown' + '.tooltip-esc', event => {
        if (event.code === 'Escape') this._close();
      });
  
      this.el.dataset.toolTipOpen = true;
      this.el.dataset.toolTip = context;
    }
  
    _close() {
      this.toolTipContent.innerHTML = '';
      this.el.dataset.toolTipOpen = 'false';
      this.el.dataset.toolTip = '';
  
      this._unlockScrolling();
  
      this.closeButton.off('click' + '.tooltip-close');
      document.documentElement.off('click' + '.tooltip-outerclick');
      document.documentElement.off('keydown' + '.tooltip-esc');
    }
  
    _lockScrolling() {
      theme.a11y.trapFocus({
        container: this.el,
        namespace: 'tooltip_focus'
      });
  
      theme.a11y.lockMobileScrolling();
      document.documentElement.classList.add('modal-open');
    }
  
    _unlockScrolling() {
      theme.a11y.removeTrapFocus({
        container: this.el,
        namespace: 'tooltip_focus'
      });
  
      theme.a11y.unlockMobileScrolling();
      document.documentElement.classList.remove('modal-open');
    }
  }
  
  customElements.define('tool-tip', ToolTip);

  /*============================================================================
    ToolTipTrigger
  ==============================================================================*/
  
  class ToolTipTrigger extends HTMLElement {
    constructor() {
      super();
      this.el = this;
      this.toolTipContent = this.querySelector('[data-tool-tip-content]');
      this.init();
    }
  
    init() {
      const toolTipOpen = new CustomEvent('tooltip:open', {
        detail: {
          context: this.dataset.toolTip,
          content: this.toolTipContent.innerHTML
        },
        bubbles: true
      });
  
      this.el.addEventListener('click', e => {
        e.stopPropagation();
        this.dispatchEvent(toolTipOpen);
      });
    }
  }
  
  customElements.define('tool-tip-trigger', ToolTipTrigger);


  /*============================================================================
    NewsletterReminder
  ==============================================================================*/
  
  class NewsletterReminder extends HTMLElement {
    constructor() {
      super();
      this.closeBtn = this.querySelector('[data-close-button]');
      this.popupTrigger = this.querySelector('[data-message]');
  
      this.id = this.dataset.sectionId;
      this.newsletterId = `NewsletterPopup-${ this.id }`;
      this.cookie = Cookies.get(`newsletter-${this.id}`);
      this.cookieName = `newsletter-${this.id}`;
      this.secondsBeforeShow = this.dataset.delaySeconds;
      this.expiry = parseInt(this.dataset.delayDays);
      this.modal = new theme.Modals(`NewsletterPopup-${this.newsletterId}`, 'newsletter-popup-modal');
  
      this.init();
    }
  
    init() {
      document.addEventListener('shopify:block:select', (evt) => {
        if (evt.detail.sectionId === this.id) {
          this.show(0, true)
        }
      });
  
      document.addEventListener('shopify:block:deselect', (evt) => {
        if (evt.detail.sectionId === this.id) {
          this.hide();
        }
      });
  
      document.addEventListener(`modalOpen.${this.newsletterId}`, () => this.hide());
      document.addEventListener(`modalClose.${this.newsletterId}`, () => this.show());
      document.addEventListener(`newsletter:openReminder`, () => this.show(0));
  
      this.closeBtn.addEventListener('click', () => {
        this.hide();
        Cookies.set(this.cookieName, 'opened', { path: '/', expires: this.expiry });
      });
  
      this.popupTrigger.addEventListener('click', () => {
        const reminderOpen = new CustomEvent('reminder:openNewsletter', { bubbles: true });
        this.dispatchEvent(reminderOpen);
  
        this.hide();
      });
    }
  
    show(time = this.secondsBeforeShow, forceOpen = false) {
      const reminderAppeared = (sessionStorage.getItem('reminderAppeared') === 'true');
  
      if (!reminderAppeared) {
        setTimeout(() => {
          this.dataset.enabled = 'true';
          sessionStorage.setItem('reminderAppeared', true);
        }, time * 1000);
      }
    }
  
    hide() {
      this.dataset.enabled = 'false';
    }
  }
  
  customElements.define('newsletter-reminder', NewsletterReminder);
  
  /*============================================================================
    ParallaxImage
  ==============================================================================*/
  
  class ParallaxImage extends HTMLElement {
    constructor() {
      super();
      this.parallaxImage = this.querySelector('[data-parallax-image]');
      this.windowInnerHeight = window.innerHeight;
      this.isActive = false;
      this.timeout = null;
      this.directionMap = {
        right: 0,
        top: 90,
        left: 180,
        bottom: 270
      }
      this.directionMultipliers = {
        0: [  1,  0 ],
        90: [  0, -1 ],
        180: [ -1,  0 ],
        270: [  0,  1 ]
      }
  
      this.init();
      window.addEventListener('scroll', () => this.scrollHandler());
    }
  
    getParallaxInfo() {
      const { width, height, top } = this.parallaxImage.getBoundingClientRect();
      let element = this.parallaxImage;
      let multipliers;
      let { angle, movement } = element.dataset;
  
      let movementPixels = angle === 'top' ? Math.ceil(height * (parseFloat(movement) / 100)) : Math.ceil(width * (parseFloat(movement) / 100));
  
      // angle has shorthands "top", "left", "bottom" and "right"
      // nullish coalescing. using `||` here would fail for `0`
      angle = this.directionMap[angle] ?? parseFloat(angle);
  
      // fallback if undefined
      // NaN is the only value that doesn't equal itself
      if (angle !== angle) angle = 270; // move to bottom (default parallax effect)
      if (movementPixels !== movementPixels) movementPixels = 100; // 100px
  
      // check if angle is located in top half and/or left half
      angle %= 360;
      if (angle < 0) angle += 360
  
      const toLeft = angle > 90 && angle < 270;
      const toTop  = angle < 180;
  
      element.style[toLeft ? 'left' : 'right'] = 0;
      element.style[toTop  ? 'top' : 'bottom'] = 0;
  
      // if it's not a perfectly horizontal or vertical movement, get cos and sin
      if (angle % 90) {
        const radians = angle * Math.PI / 180
        multipliers = [ Math.cos(radians), Math.sin(radians) * -1 ] // only sin has to be inverted
      } else {
        multipliers = this.directionMultipliers[angle];
      }
  
      // increase width and height according to movement and multipliers
      if (multipliers[0]) element.style.width  = `calc(100% + ${movementPixels * Math.abs(multipliers[0])}px)`;
      if (multipliers[1]) element.style.height = `calc(100% + ${movementPixels * Math.abs(multipliers[1])}px)`;
  
      return {
        element,
        movementPixels,
        multipliers,
        top,
        height
      }
    }
  
    init() {
      const { element, movementPixels, multipliers, top, height } = this.getParallaxInfo();;
  
      const scrolledInContainer = this.windowInnerHeight - top;
      const scrollArea = this.windowInnerHeight + height;
      const progress = scrolledInContainer / scrollArea;
  
      if (progress > -0.1 && progress < 1.1) {
        const position = Math.min(Math.max(progress, 0), 1) * movementPixels;
        element.style.transform = `translate3d(${position * multipliers[0]}px, ${position * multipliers[1]}px, 0)`;
      }
  
      if (this.isActive) requestAnimationFrame(this.init.bind(this));
    }
  
    scrollHandler() {
      if (this.isActive) {
        clearTimeout(this.timeout);
      } else {
        this.isActive = true;
        requestAnimationFrame(this.init.bind(this));
      }
  
      this.timeout = setTimeout(() => this.isActive = false, 20);
    }
  }
  
  customElements.define('parallax-image', ParallaxImage);
  
  /*============================================================================
    ImageElement
  ==============================================================================*/
  
  class ImageElement extends HTMLElement {
    constructor() {
      super();
  
      this.init();
    }
  
    init() {
  
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
  
        this.removeAnimations();
  
        observer.unobserve(this);
      }
  
      // Set an IntersectionObserver to check if the image is nearly in the viewport
      new IntersectionObserver(handleIntersection.bind(this), {rootMargin: '0px 0px 400px 0px'}).observe(this);
    }
  
    removeAnimations() {
      // Find the closest image-wrap and remove the animation (fix for streamline image-wrap shimmer)
      const imageWrap = this.closest('.image-wrap');
      const skrimWrap = this.closest('.skrim__link');
  
      if (imageWrap) {
        imageWrap.classList.add('loaded');
      }
  
      if (skrimWrap) {
        skrimWrap.classList.add('loaded');
      }
    }
  }
  
  customElements.define('image-element', ImageElement);
  
  /*============================================================================
    PredictiveSearch
  ==============================================================================*/
  
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.enabled = this.getAttribute('data-enabled');
      this.context = this.getAttribute('data-context');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('#predictive-search');
      this.closeBtn = this.querySelector('.btn--close-search');
      this.screen = this.querySelector('[data-screen]');
      this.SearchModal = this.closest('#SearchModal') || null;
  
      // Open events
      document.addEventListener('predictive-search:open', e => {
        if (e.detail.context !== this.context) return;
        this.classList.add('is-active');
  
        // Wait for opening events to finish then apply focus
        setTimeout(() => { this.input.focus(); }, 100);
  
        document.body.classList.add('predictive-overflow-hidden');
      });
  
      // listen for class change of 'modal--is-active on this.SearchModal
      if (this.SearchModal) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class') {
              const modalClass = mutation.target.className;
              if (modalClass.indexOf('modal--is-active') > -1) {
                setTimeout(() => { this.input.focus(); }, 100);
              }
            }
          });
        });
  
        observer.observe(this.SearchModal, {
          attributes: true
        });
      }
  
      if (this.enabled === 'false') return;
  
      // On typing
      this.input.addEventListener('keydown', () => { this.classList.add('is-active'); });
  
      this.input.addEventListener('input', this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
  
      // Close events
      document.addEventListener('predictive-search:close', () => { this.close(); });
      document.addEventListener('keydown', (event) => { if (event.keyCode === 27) this.close(); });
      this.closeBtn.addEventListener('click', e => { e.preventDefault(); this.close(); });
      this.screen.addEventListener('click', () => { this.close(); });
    }
  
    onChange() {
      const searchTerm = this.input.value.trim();
  
      if (!searchTerm.length) return;
  
      this.getSearchResults(searchTerm);
    }
  
    getSearchResults(searchTerm) {
      const searchObj = {
        'q': searchTerm,
        'resources[limit]': 3,
        'resources[limit_scope]': 'each'
      };
  
      const params = this.paramUrl(searchObj);
  
      fetch(`${theme.routes.predictiveSearch}?${params}&section_id=search-results`)
        .then((response) => {
          if (!response.ok) {
            const error = new Error(response.status);
            this.close();
            throw error;
          }
  
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-search-results').innerHTML;
          this.predictiveSearchResults.innerHTML = resultsMarkup;
          this.open();
  
          AOS.refreshHard();
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  
    open() {
      this.predictiveSearchResults.style.display = 'block';
    }
  
    close() {
      this.predictiveSearchResults.style.display = 'none';
      this.predictiveSearchResults.innerHTML = '';
      this.classList.remove('is-active');
      document.body.classList.remove('predictive-overflow-hidden');
  
      document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
    }
  
    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  
    paramUrl(obj) {
      return Object.keys(obj).map(function(key) {
        return key + '=' + encodeURIComponent(obj[key]);
      }).join('&')
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  
  class RecipientForm extends HTMLElement {
    constructor() {
      super();
      this.checkboxInput = this.querySelector(`#Recipient-Checkbox-${ this.dataset.sectionId }`);
      this.emailInput = this.querySelector(`#Recipient-email-${ this.dataset.sectionId }`);
      this.nameInput = this.querySelector(`#Recipient-name-${ this.dataset.sectionId }`);
      this.messageInput = this.querySelector(`#Recipient-message-${ this.dataset.sectionId }`);
      this.dateInput = this.querySelector(`#Recipient-send-on-${ this.dataset.sectionId }`);
      this.addEventListener('change', () => this.onChange());
      this.recipientFields = this.querySelector('.recipient-fields');
  
      this.checkboxInput.addEventListener('change', () => {
        this.recipientFields.style.display = this.checkboxInput.checked ? 'block' : 'none';
      });
    }
  
    connectedCallback() {
      document.addEventListener('ajaxProduct:error', (event) => {
        const productVariantID = event.target.querySelector('[name="id"]').value;
        if (productVariantID === this.dataset.productVariantId) {
          this.displayErrorMessage(event.detail.errorMessage);
        }
      });
  
      document.addEventListener('ajaxProduct:added', (event) => {
        console.log("======wuguide=====");
        
        const productVariantID = event.target.querySelector('[name="id"]').value;
        if (productVariantID === this.dataset.productVariantId) {
          this.clearInputFields();
          this.clearErrorMessage();
        }
      });
    }
  
    onChange() {
      if (!this.checkboxInput.checked) {
        this.clearInputFields();
        this.clearErrorMessage();
      }
    }
  
    clearInputFields() {
      this.querySelectorAll('.field__input').forEach(el => el.value = '');
    }
  
    displayErrorMessage(body) {
      this.clearErrorMessage();
      if (body) {
        return Object.entries(body).forEach(([key, value]) => {
          const inputElement = this[`${key}Input`];
          if (!inputElement) return;
  
          inputElement.setAttribute('aria-invalid', true);
          inputElement.classList.add('field__input--error');
        });
      }
    }
  
    clearErrorMessage() {
      this.querySelectorAll('.field__input').forEach(inputElement => {
        inputElement.setAttribute('aria-invalid', false);
        inputElement.removeAttribute('aria-describedby');
        inputElement.classList.remove('field__input--error');
      });
    }
  };
  
  customElements.define('recipient-form', RecipientForm);




// Page Product Detail Component
class PageProductDetail extends HTMLElement {
  constructor() {
    super();
    this.handle = this.dataset.handle;
    this.className = this.dataset.value;
    this.number = this.dataset.number || null;

    if (!this.handle) return;
    // this.load();
    this.init();
  }
  init() {
    var that = this;
    var data = document.querySelectorAll(`.${this.className}.shopify-section`);
   if(data.length == 0){
    // 删除该组件
    that.parentNode.remove();
    // 删除对应的导航栏
    var list = document.querySelectorAll(".product-nav .product-nav-link");
    list.forEach((item)=>{
      item.querySelectorAll("li").forEach((li)=>{
        if(li.querySelector("a").textContent.trim() == this.number){
          li.remove();
        }
      });
    });
    UIkit.switcher(".product-nav-link", {connect:".product-switcher-content",swiping:false});

   }else{
      data.forEach((item)=>{
        const element = item.querySelector(".uk-hidden");
        if (element) {
          that.innerHTML = element.innerHTML;
        }
      })
   }
    


  }
  async load() {
    const res = await fetch(`/product/${this.handle}?view=ajax`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const list = doc.querySelectorAll(`.${this.className}`);
    
    list.forEach((item)=>{
      this.appendChild(item);  
    })    
  }
}

if (!customElements.get('page-product-detail')) {
  customElements.define('page-product-detail', PageProductDetail);
}

// Buy Now Button Component
class BuyNow extends HTMLElement {
  constructor() {
    super();
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onPurchaseSummaryChange = this.onPurchaseSummaryChange.bind(this);
    this.init();
  }

  /** 监听哪些属性 */
  static get observedAttributes() {
    return ['data-id', 'data-number','data-available'];
  }

  /** 属性变化时触发 */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-id') {
      this.id = Number(newValue);
    }

    if (name === 'data-number') {
      this.number = Number(newValue);
    }
    if (name === 'data-available') {
      this.available = newValue === "true";
    }
  }

  connectedCallback() {
    document.addEventListener('quantity:change', this.onQuantityChange);
    document.addEventListener('purchase-summary:change', this.onPurchaseSummaryChange);
    // 初始读取一次
    this.id = Number(this.dataset.id);
    this.number = Number(this.dataset.number);
    this.available = this.dataset.available === "true";
    if (window.productPurchaseSummary) {
      this.onPurchaseSummaryChange({ detail: window.productPurchaseSummary });
    }
  }
  disconnectedCallback() {
    document.removeEventListener('quantity:change', this.onQuantityChange);
    document.removeEventListener('purchase-summary:change', this.onPurchaseSummaryChange);
  }
  onPurchaseSummaryChange(e) {
    const { id, quantity, available, total, compare, totalFormatted, compareFormatted } = e.detail || {};
    const fixedBar = this.closest('.product-fixed-contain');
    if (!fixedBar) return;

    if (id) this.dataset.id = id;
    if (quantity) this.dataset.number = quantity;
    if (available !== undefined) this.dataset.available = available;

    const salePrice = fixedBar.querySelector('.sale-price');
    const comparePrice = fixedBar.querySelector('.compare-price');
    const formatMoney = (cents, fallback) => {
      if (Number.isFinite(cents) && window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      return fallback || '';
    };
    const fixedTotal = formatMoney(total, totalFormatted);
    const fixedCompare = compare > total ? formatMoney(compare, compareFormatted) : '';
    if (salePrice && fixedTotal) salePrice.innerHTML = fixedTotal;
    if (comparePrice) {
      comparePrice.innerHTML = fixedCompare;
      comparePrice.style.display = fixedCompare ? 'block' : 'none';
    }
  }
  onQuantityChange(e) {
    const {id, number, available} = e.detail;
    const addToCart = this.querySelector(".add-to-cart");
    if(id){
      this.dataset.id = id;
    }
    if(number){
      this.dataset.number = number;
    }
    if(available !== undefined){
      this.dataset.available = available;
    }
    if(available == false){
      this.querySelector(".buy-now").textContent = "Sold Out";
      this.querySelector(".buy-now").classList.add('is-disabled');
      if (addToCart) addToCart.style.display = "none";
      this.classList.add('is-disabled');
    }else{
  
      this.querySelector(".buy-now").textContent = "Buy it now";
      this.querySelector(".buy-now").classList.remove('is-disabled');
      this.classList.remove('is-disabled');
      if (addToCart) addToCart.style.display = "flex";
    }


  };
  init() {
    // 绑定点击事件
    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const targetClass = event.target.className;

      // Both product layouts checkout the current main product and selected accessories.
      if (this.closest('.product-fixed-contain') && event.target.closest('.buy-now')) {
        const summary = document.querySelector('.product-section:not([data-modal="true"]) purchase-summary');
        if (summary) {
          summary.buyNow();
          return;
        }
      }

      if(this.available === false) return;
      if (!this.id) return;
      event.target.classList.add('is-loading');
      try {
        const res = await fetch(`${theme.routes.cartAdd}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: this.id,
            quantity: this.number == 0 ? 1 : this.number
          })
        }).then(response => response.json());


         if (res.status === 422) {
          alert(res.description || 'Please try again.');
          event.target.classList.remove('is-loading');
          return;
         } else{
              event.target.classList.remove('is-loading');
              if(targetClass.includes('buy-now')){
                window.location.href = `/cart/${this.id}:${this.number}`;
              }else{
                // 通知购物车更新
                 if (theme.settings.cartType === 'page') {
                    window.location = theme.routes.cartPage;
                  }else{
                    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                      detail: {
                        product: res.data,
                        addToCartBtn: this.target
                      },
                      bubbles: true
                    }));
                  }
              
              }
         }
      } catch (err) {
        console.error(err);
        event.target.classList.remove('is-loading');
      }
    });

  }
}

if (!customElements.get('buy-now')) {
  customElements.define('buy-now', BuyNow);
}



// 产品详情页面中的部件幕模块内容
class BubbleImage extends HTMLElement {
  constructor() {
    super();
    this.handle = this.dataset.handle;
    if (!this.handle) return;
    var data = [
      {
        handle:'9962572579138',
        class:'one',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001.png?v=1771036854",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-mobile.png?v=1771036861",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-1.png?v=1772075439' />
          <strong>StabilTrac™Fin</strong>
          <p>Nose Design Streamlined speed nose with V-entry and dual-surface carbon stringers, plus side reinforcements—maximizing glide, stiffness, and precision for elite-level acceleration.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-2.png?v=1772075586' />
          <strong>EZ-Air SUP Valve</strong>
          <p>The StabilTrac™Fin  and three removable rear fins help provide a smoother glide and make it easier to track straight while paddling.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-3.png?v=1772075651' />
          <strong>EZ-Grip Handle</strong>
          <p>The center handle offers effortless portability, while the rear handle ensures smooth water dragging and easy transitions, making your paddleboarding experience seamless.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-4.png?v=1772075752' />
          <strong>Enough D-Rings</strong>
          <p>With 9 D-rings, expand your paddleboarding options—secure extra gear, attach accessories, or customize your setup for any adventure, ensuring maximum flexibility on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-5.png?v=1772075901' />
          <strong>Dual Bungee Systems</strong>
          <p>The dual bungee systems at the front and rear provide secure, hassle-free storage for your gear, keeping essentials within easy reach.</p>`
        ]  
      },
      {
        handle:'9967459008834',
        class:'two',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001.png?v=1769392360",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126002.png?v=1771037022",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001-icon-1.png?v=1772076138' />
          <strong>StabilTrac™Fin</strong>
          <p>Nose Design Streamlined speed nose with V-entry and dual-surface carbon stringers, plus side reinforcements—maximizing glide, stiffness, and precision for elite-level acceleration.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001-icon-2.png?v=1772076201' />
          <strong>EZ-Air SUP Valve</strong>
          <p>The StabilTrac™Fin  and three removable rear fins help provide a smoother glide and make it easier to track straight while paddling.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001-icon-3.png?v=1772076333' />
          <strong>EZ-Grip Handle</strong>
          <p>The center handle offers effortless portability, while the rear handle ensures smooth water dragging and easy transitions, making your paddleboarding experience seamless.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001-icon-4.png?v=1772076403' />
          <strong>Enough D-Rings</strong>
          <p>With 9 D-rings, expand your paddleboarding options—secure extra gear, attach accessories, or customize your setup for any adventure, ensuring maximum flexibility on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3200126001-icon-5.png?v=1772076467' />
          <strong>Bungee Systems</strong>
          <p>The integrated bungee system offers hassle-free, secure storage for your gear, keeping essentials within easy reach. Plus, six additional D-rings at the rear provide expanded cargo capacity—perfect for longer trips or extra equipment.</p>`
        ]
      },
      {
        handle:'9967461073218',
        class:'three',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001.png?v=1771037081",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126002.png?v=1771037101",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001-icon-1.png?v=1772076663' />
          <strong>StabilTrac™Fin</strong>
          <p>Nose Design Streamlined speed nose with V-entry and dual-surface carbon stringers, plus side reinforcements—maximizing glide, stiffness, and precision for elite-level acceleration.</p>`, 
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001-icon-2.png?v=1772076728' />
          <strong>EZ-Air SUP Valve</strong>
          <p>The StabilTrac™Fin  and three removable rear fins help provide a smoother glide and make it easier to track straight while paddling.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001-icon-4.jpg?v=1772595505' />
          <strong>EZ-Grip Handle</strong>
          <p>The center handle offers effortless portability, while the rear handle ensures smooth water dragging and easy transitions, making your paddleboarding experience seamless.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001-icon-3.jpg?v=1772595264' />
          <strong>Enough D-Rings</strong>
          <p>With 9 D-rings, expand your paddleboarding options—secure extra gear, attach accessories, or customize your setup for any adventure, ensuring maximum flexibility on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/3050126001-icon-5.png?v=1772076913' />
          <strong>Bungee Systems</strong>
          <p>The integrated bungee system offers hassle-free, secure storage for your gear, keeping essentials within easy reach.</p>`
        ] 
      },
      {
        handle:'10011620213058',
        class:'four',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001.png?v=1771037150",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126002.png?v=1771037171",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-1.png?v=1772085095' />
          <strong>Pointed Nose</strong>
          <p> Nose Design Streamlined speed nose with V-entry and dual-surface carbon stringers, plus side reinforcements—maximizing glide, stiffness, and precision for elite-level acceleration.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-2.png?v=1772085157' />
          <strong>Adjustable Race Fin</strong>
          <p>Precision tracking or agile turns—adjust fin placement ±2cm to suit course demands. Deep profile enhances stability, drive, and speed during flatwater races.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-3.png?v=1772085310' />
          <strong>5 mm Diamond-Pattern Deck Pad</strong>
          <p>Thick, abrasion-resistant EVA  with diamond texture offers superior grip and comfort underfoot, even when the deck is wet.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-4.png?v=1772085368' />
          <strong>Multifunctional Base</strong>
          <p>Integrated mount supports action cameras and GPS units, enabling precise performance tracking and video capture without compromising the board’s streamlined racing profile.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-5.png?v=1772085447' />
          <strong>Front D-Ring Leash Attachment</strong>
          <p>Stainless steel D-rings at the nose provide secure foot-leash tethering, ensuring safety and quick board retrieval during wipe-outs.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-6.png?v=1772085505' />
          <strong>Front Bungee System</strong>
          <p>Streamlined front-only bungee stores essentials without disrupting aerodynamics—ideal for race hydration or emergency gear during long-distance sprints.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/racing0126001-icon-7.png?v=1772085582' />
          <strong>Three-Handle Configuration</strong>
          <p>One center handle ensures easy carrying; two angled front grips aid sprint starts, re-boarding, and directional control during prone paddling—engineered for competitive edge.
fter a long session, making every outing smoother from start to finish.</p>`
        ]
      },
      {
        handle:'9985227129154',
        class:'five',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001.png?v=1776063189",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126002.png?v=1776063245",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-1.png?v=1772087236' />
          <strong>Pointed Nose</strong>
          <p>The pointed nose improves glide and tracking in all waters.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-2.png?v=1772087295' />
          <strong>Multifunctional Base</strong>
          <p>Customize your Niphean paddleboard with 2 multifunctional bases. Attach GPS, trackers, cameras, or phones for easy access and enhanced performance tracking on your adventures.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-3.png?v=1772087443' />
          <strong>Front Bungee System</strong>
          <p>The All-Round’s wide adjustable bungee cords at both the front and rear offer dynamic, secure storage for your essentials. Adjustable tension lets you adapt the space to fit your gear, making every adventure more organized and hands-free.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-4.png?v=1772087558' />
          <strong>Multi Functional Platform</strong>
          <p>Equipped with modular mounting bases, your Niphean board is ready for fishing rods, coolers, cameras, sonar devices, and more. Switch from paddling to angling to relaxing—effortlessly. It’s not just a board, it’s your mobile outdoor platform.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-5.png?v=1772087634' />
          <strong>Handles</strong>
          <p>Strategic Carry Handles for Convenience Designed with three well-placed handles—at the nose, center, and tail—this board ensures easy solo transport and maneuvering. The rear handle is especially useful for pulling the board ashore after a long session, making every outing smoother from start to finish.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-6.png?v=1772087706' />
          <strong>Paddle Holder</strong>
          <p>Keep your paddle secure when not in use. The dedicated paddle holder D-ring offers a simple, reliable way to clip your paddle to the board, perfect for breaks, yoga, or when transitioning between paddling and relaxing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-7.png?v=1772087792' />
          <strong>D-rings</strong>
          <p>Equipped with 17 strategically placed D-rings, the board is ready for anything — from securing gear and attaching kayak seats to towing or connecting accessories. Customize your setup for touring, fishing, or multi-day trips with confidence and flexibility.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-8.png?v=1772087925' />
          <strong>Rear Bungee System</strong>
          <p>Rear storage system adds extra capacity for gear and essentials—perfect for longer paddles and adventure-packed SUP trips.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-9.png?v=1772088034' />
          <strong>EZ-Air SUP Valve</strong>
          <p>Designed for convenience and allows you to inflate and deflate your paddle board quickly and easily, so you can spend less time setting up and more time on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-10.png?v=1772088092' />
          <strong>Electric Pressure Gauge</strong>
          <p>Provides accurate real-time PSI readings, allowing precise inflation control to achieve optimal board stiffness and consistent on-water performance.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-11.png?v=1772088158' />
          <strong>Carbon Reinforcement</strong>
          <p>Real carbon fiber reinforcements boost rigidity and impact resistance, reducing vibration for a stable, responsive ride that builds confidence with every paddle stroke.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-12.png?v=1772088266' />
          <strong>Square Tail</strong>
          <p>The square tail boosts stability when turning or carrying gear.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-13.png?v=1772088317' />
          <strong>Stabil Trac Fin</strong>
          <p>Experience stability perfected and tracking for smooth by balanced wing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-14.png?v=1772088373' />
          <strong>Removable Rear Fin</strong>`
        ]
      },
      {
        handle:'9985163198786',
        class:'six',
        image:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001.png?v=1776061548",
        mobile:"https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126002.png?v=1776061574",
        list:[
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-1.png?v=1772089122' />
          <strong>Pointed Nose</strong>
          <p>The pointed nose improves glide and tracking in all waters.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-2.png?v=1772089189' />
          <strong>Multifunctional Base</strong>
          <p>Customize your Niphean paddleboard with 2 multifunctional bases. Attach GPS, trackers, cameras, or phones for easy access and enhanced performance tracking on your adventures.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-3.png?v=1772089483' />
          <strong>Front Bungee System</strong>
          <p>The All-Round’s wide adjustable bungee cords at both the front and rear offer dynamic, secure storage for your essentials. Adjustable tension lets you adapt the space to fit your gear, making every adventure more organized and hands-free.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-4.png?v=1772089590' />
          <strong>Multi Functional Platform</strong>
          <p>Equipped with modular mounting bases, your Niphean board is ready for fishing rods, coolers, cameras, sonar devices, and more. Switch from paddling to angling to relaxing—effortlessly. It’s not just a board, it’s your mobile outdoor platform. </p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-5.png?v=1772089654' />
          <strong>7 Handles</strong>
          <p>Equipped with seven sturdy carry handles — one at the nose, one at the tail, plus two on each bungee system — this board is designed for effortless transport and teamwork. Whether you're carrying solo, with a partner, or pulling the board onto shore, every handle is right where you need it.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-7.png?v=1772089775' />
          <strong>Paddle Holder</strong>
          <p>Keep your paddle secure when not in use. The dedicated paddle holder D-ring offers a simple, reliable way to clip your paddle to the board, perfect for breaks, yoga, or when transitioning between paddling and relaxing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-6.png?v=1772089711' />
          <strong>D-rings</strong>
          <p>Equipped with 17 strategically placed D-rings, the board is ready for anything — from securing gear and attaching kayak seats to towing or connecting accessories. Customize your setup for touring, fishing, or multi-day trips with confidence and flexibility.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-8.png?v=1772089845' />
          <strong>Rear Bungee System</strong>
          <p>Rear storage system adds extra capacity for gear and essentials—perfect for longer paddles and adventure-packed SUP trips.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-9.png?v=1772089917' />
          <strong>EZ-Air SUP Valve</strong>
          <p>Designed for convenience and allows you to inflate and deflate your paddle board quickly and easily, so you can spend less time setting up and more time on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-10.png?v=1776062366' />
          <strong>Electric Pressure Gauge</strong> 
          <p>Provides accurate real-time PSI readings, allowing precise inflation control to achieve optimal board stiffness and consistent on-water performance.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-11.png?v=1772090080' />
          <strong>Carbon Reinforcement</strong>
          <p>Real carbon fiber reinforcements boost rigidity and impact resistance, reducing vibration for a stable, responsive ride that builds confidence with every paddle stroke.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-12.png?v=1772090139' />
          <strong>Square Tail</strong>
          <p>The square tail boosts stability when turning or carrying gear.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-13.png?v=1772090196' />
          <strong>Stabil Trac Fin</strong>
          <p>Experience stability perfected and tracking for smooth by balanced wing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/ars0126001-icon-14.png?v=1772090246' />
          <strong>Removable Rear Fin</strong>`
        ]
      }
    ];

    this.variantData = data.find(item=>item.handle === this.handle);

    this.init();
  }
   init() {
    const isHoverDevice = window.matchMedia('(hover: hover)').matches;
    var bubbleContent = `<div class="product-bubble">
          <img class="image uk-visible@s" src="${this.variantData.image}"  width="750" height="343" loading="lazy"/>
          <img class="image uk-hidden@s" src="${this.variantData.mobile}"  width="343" height="750" loading="lazy"/>  
            ${this.variantData.list.map((item, index) => `
            <div class="hover-icon hover-icon-${this.variantData.class}-${index + 1}" data-tippy-content="<div class='bubble-item-contain'>${item}</div>"></div>`).join('')}
      </div>`;
    this.innerHTML = bubbleContent;
    tippy('[data-tippy-content]', {
          placement: 'top',
          animation: 'shift-away',
          theme: 'light',
          trigger: isHoverDevice ? 'mouseenter' : 'click',
          allowHTML: true
        });
  }
}

if (!customElements.get('bubble-image')) {
  customElements.define('bubble-image', BubbleImage);
}

class BubbleNewImage extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.handle = this.dataset.handle;

    const data = [
      {
        handle: '9985227129154',
        image: 'https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260825001.png?v=1787643215',
        mobile: 'https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260825002.png?v=1787640151',
        desktopPositions: [[98.4,25.36],[93.8,25.36],[80.73,26.44],[64,16.95],[48.93,25.36],[57.07,40.02],[36,11.7],[19.33,24.74],[8.63,25.63],null,[74,25.63],[2.6,19.74],[50.6,90.38],[9.07,81.92]],
        mobilePositions: [[24.49,3.33],[24.49,8.27],[34.4,20.67],[13.87,36.2],[24.49,51.67],[44.35,44],[5.99,42.87],[32.07,79.33],[23.9,91.07],null,[31.2,27.33],[24.36,97.67],[86.3,49.47],[83.59,90.93]],
        list: [
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-1.png?v=1772087236'><strong>Pointed Nose</strong><p>The pointed nose improves glide and tracking in all waters.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-2.png?v=1772087295'><strong>Multifunctional Base</strong><p>Customize your Niphean paddleboard with 2 multifunctional bases. Attach GPS, trackers, cameras, or phones for easy access and enhanced performance tracking on your adventures.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-3.png?v=1772087443'><strong>Front Bungee System</strong><p>The All-Round’s wide adjustable bungee cords at both the front and rear offer dynamic, secure storage for your essentials. Adjustable tension lets you adapt the space to fit your gear, making every adventure more organized and hands-free.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-4.png?v=1772087558'><strong>Multi Functional Platform</strong><p>Equipped with modular mounting bases, your Niphean board is ready for fishing rods, coolers, cameras, sonar devices, and more. Switch from paddling to angling to relaxing—effortlessly. It’s not just a board, it’s your mobile outdoor platform.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-5.png?v=1772087634'><strong>Handles</strong><p>Strategic Carry Handles for Convenience Designed with three well-placed handles—at the nose, center, and tail—this board ensures easy solo transport and maneuvering. The rear handle is especially useful for pulling the board ashore after a long session, making every outing smoother from start to finish.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-6.png?v=1772087706'><strong>Paddle Holder</strong><p>Keep your paddle secure when not in use. The dedicated paddle holder D-ring offers a simple, reliable way to clip your paddle to the board, perfect for breaks, yoga, or when transitioning between paddling and relaxing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-7.png?v=1772087792'><strong>D-rings</strong><p>Equipped with 17 strategically placed D-rings, the board is ready for anything — from securing gear and attaching kayak seats to towing or connecting accessories. Customize your setup for touring, fishing, or multi-day trips with confidence and flexibility.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-8.png?v=1772087925'><strong>Rear Bungee System</strong><p>Rear storage system adds extra capacity for gear and essentials—perfect for longer paddles and adventure-packed SUP trips.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-9.png?v=1772088034'><strong>EZ-Air SUP Valve</strong><p>Designed for convenience and allows you to inflate and deflate your paddle board quickly and easily, so you can spend less time setting up and more time on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-10.png?v=1772088092'><strong>Electric Pressure Gauge</strong><p>Provides accurate real-time PSI readings, allowing precise inflation control to achieve optimal board stiffness and consistent on-water performance.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-11.png?v=1772088158'><strong>Carbon Reinforcement</strong><p>Real carbon fiber reinforcements boost rigidity and impact resistance, reducing vibration for a stable, responsive ride that builds confidence with every paddle stroke.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-12.png?v=1772088266'><strong>Square Tail</strong><p>The square tail boosts stability when turning or carrying gear.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-13.png?v=1772088317'><strong>Stabil Trac Fin</strong><p>Experience stability perfected and tracking for smooth by balanced wing.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/turing0126001-icon-14.png?v=1772088373'><strong>Removable Rear Fin</strong>`
        ]
      },
      {
        handle: '9962572579138',
        image: 'https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260825013.png?v=1787651674',
        mobile: 'https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260825014.png?v=1787651675',
        desktopPositions: [[51.67,83.96],[9.5,26.78],[44.33,26.82],[32.67,43.98],[82,24.78]],
        mobilePositions: [[92.72,48.07],[27.7,89.67],[27.7,54],[7.12,68],[29.15,20.67]],
        list: [
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-1.png?v=1772075439'><strong>StabilTrac™Fin</strong><p>Nose Design Streamlined speed nose with V-entry and dual-surface carbon stringers, plus side reinforcements—maximizing glide, stiffness, and precision for elite-level acceleration.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-2.png?v=1772075586'><strong>EZ-Air SUP Valve</strong><p>The StabilTrac™Fin and three removable rear fins help provide a smoother glide and make it easier to track straight while paddling.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-3.png?v=1772075651'><strong>EZ-Grip Handle</strong><p>The center handle offers effortless portability, while the rear handle ensures smooth water dragging and easy transitions, making your paddleboarding experience seamless.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-4.png?v=1772075752'><strong>Enough D-Rings</strong><p>With 9 D-rings, expand your paddleboarding options—secure extra gear, attach accessories, or customize your setup for any adventure, ensuring maximum flexibility on the water.</p>`,
          `<img width='220' height='110' loading='lazy' src='https://cdn.shopify.com/s/files/1/0754/1089/9266/files/20260123001-icon-5.png?v=1772075901'><strong>Dual Bungee Systems</strong><p>The dual bungee systems at the front and rear provide secure, hassle-free storage for your gear, keeping essentials within easy reach.</p>`
        ]
      }
    ];

    this.variantData = data.find((item) => item.handle === this.handle);
    if (!this.variantData) return;
    this.init();
  }

  init() {
    this.innerHTML = `<div class="bubble-new-image__frame">
      <div class="bubble-new-image__media">
        <picture class="bubble-new-image__picture">
          <source media="(max-width: 749px)" srcset="${this.variantData.mobile}" width="343" height="750">
          <img class="bubble-new-image__image" src="${this.variantData.image}" width="750" height="343" loading="lazy" alt="Product feature overview">
        </picture>
        <svg class="bubble-new-image__line" aria-hidden="true" preserveAspectRatio="none"><line></line></svg>
        ${this.variantData.list.map((item, index) => {
          const desktop = this.variantData.desktopPositions[index];
          const mobile = this.variantData.mobilePositions[index];
          if (!desktop || !mobile) return '';
          return `<button class="bubble-new-image__hotspot" type="button" aria-label="View feature ${index + 1}" data-bubble-index="${index}" style="--hotspot-x:${desktop[0]}%;--hotspot-y:${desktop[1]}%;--hotspot-mobile-x:${mobile[0]}%;--hotspot-mobile-y:${mobile[1]}%"></button>`;
        }).join('')}
        <div class="bubble-new-image__popover" role="tooltip" hidden></div>
      </div>
    </div>`;

    this.frame = this.querySelector('.bubble-new-image__media');
    this.featurePopover = this.querySelector('.bubble-new-image__popover');
    this.line = this.querySelector('.bubble-new-image__line line');
    this.hotspots = [...this.querySelectorAll('[data-bubble-index]')];
    const supportsHover = window.matchMedia('(hover: hover)').matches;

    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener('mouseenter', () => {
        if (supportsHover) this.showFeaturePopover(hotspot);
      });
      hotspot.addEventListener('mouseleave', () => {
        if (supportsHover) this.hideFeaturePopover();
      });
      hotspot.addEventListener('focus', () => this.showFeaturePopover(hotspot));
      hotspot.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!supportsHover) this.showFeaturePopover(hotspot);
      });
    });

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.hideFeaturePopover();
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.hideFeaturePopover();
    });
    window.addEventListener('resize', () => this.hideFeaturePopover(), { passive: true });
  }

  showFeaturePopover(hotspot) {
    const index = Number(hotspot.dataset.bubbleIndex);
    this.activeHotspot = hotspot;
    this.featurePopover.innerHTML = `<div class="bubble-item-contain">${this.variantData.list[index]}</div>`;
    this.featurePopover.hidden = false;
    this.line.parentElement.classList.remove('is-visible');

    requestAnimationFrame(() => {
      if (this.activeHotspot !== hotspot || this.featurePopover.hidden) return;
      const frameWidth = this.frame.clientWidth;
      const frameHeight = this.frame.clientHeight;
      this.line.parentElement.setAttribute('viewBox', `0 0 ${frameWidth} ${frameHeight}`);
      const hotspotX = hotspot.offsetLeft;
      const hotspotY = hotspot.offsetTop;
      const gap = 26;
      const inset = 10;
      const popoverWidth = this.featurePopover.offsetWidth;
      const popoverHeight = Math.min(this.featurePopover.offsetHeight, frameHeight - inset * 2);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      let left;
      let top;

      if (isMobile) {
        left = Math.min(Math.max(hotspotX - popoverWidth / 2, inset), frameWidth - popoverWidth - inset);
        top = hotspotY + gap;
        if (top + popoverHeight > frameHeight - inset) top = hotspotY - popoverHeight - gap;
      } else {
        left = hotspotX + gap;
        if (left + popoverWidth > frameWidth - inset) left = hotspotX - popoverWidth - gap;
        top = hotspotY - popoverHeight / 2;
      }

      left = Math.min(Math.max(left, inset), frameWidth - popoverWidth - inset);
      top = Math.min(Math.max(top, inset), frameHeight - popoverHeight - inset);
      this.featurePopover.style.left = `${left}px`;
      this.featurePopover.style.top = `${top}px`;
      this.featurePopover.style.maxHeight = `${frameHeight - inset * 2}px`;

      const popoverRight = left + popoverWidth;
      const popoverBottom = top + popoverHeight;
      let targetX;
      let targetY;
      if (isMobile) {
        targetX = Math.min(Math.max(hotspotX, left), popoverRight);
        targetY = Math.min(Math.max(hotspotY, top), popoverBottom);
        if (hotspotX < left) targetX = left;
        else if (hotspotX > popoverRight) targetX = popoverRight;
        else if (hotspotY < top) targetY = top;
        else targetY = popoverBottom;
      } else {
        targetX = left > hotspotX ? left : popoverRight;
        targetY = hotspotY;
      }

      this.line.setAttribute('x1', hotspotX);
      this.line.setAttribute('y1', hotspotY);
      this.line.setAttribute('x2', targetX);
      this.line.setAttribute('y2', targetY);
      this.line.parentElement.classList.add('is-visible');
    });
  }

  hideFeaturePopover() {
    if (!this.featurePopover) return;
    this.activeHotspot = null;
    this.featurePopover.hidden = true;
    this.line.parentElement.classList.remove('is-visible');
  }
}

if (!customElements.get('bubble--new-image')) {
  customElements.define('bubble--new-image', BubbleNewImage);
}






// 这里是给值加上缩放功能

(() => {
  const DESIGN_WIDTH = 1920;
  const DESIGN_HEIGHT = 1200;
  let resizeTimer = null;

  const getScaleRatio = () => {
    const screen = document.querySelector('#PageContainer');
    const viewport = window.visualViewport;
    const rect = screen ? screen.getBoundingClientRect() : null;
    const viewportWidth = rect?.width || viewport?.width || document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = rect?.height || viewport?.height || document.documentElement.clientHeight || window.innerHeight;
    const widthRatio = viewportWidth / DESIGN_WIDTH;
    const heightRatio = viewportHeight / DESIGN_HEIGHT;

    return Math.min(widthRatio, heightRatio);
  };

  const updateScaleRatio = () => {
    const ratio = getScaleRatio();
    document.documentElement.style.setProperty('--scale-ratio', ratio.toFixed(4));

    return ratio;
  };

  const handleScaleRatioResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(updateScaleRatio, 120);
  };

  window.updateScaleRatio = updateScaleRatio;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateScaleRatio, { once: true });
  } else {
    updateScaleRatio();
  }

  window.addEventListener('resize', handleScaleRatioResize);
  window.addEventListener('load', updateScaleRatio);
  window.addEventListener('orientationchange', handleScaleRatioResize);
  window.visualViewport?.addEventListener('resize', handleScaleRatioResize);
})();

// Paddle Boards mega menu hover fallback.
// Ensures the custom menu opens even after #SiteHeader becomes .site-header--stuck.
(() => {
  if (window.paddleBoardsMenuHoverBound) {
    return;
  }

  window.paddleBoardsMenuHoverBound = true;

  const setNavHoverWhite = (isActive) => {
    const headerWrapper = document.getElementById('HeaderWrapper');

    if (!headerWrapper) {
      return;
    }

    if (isActive) {
      headerWrapper.classList.add('header-wrapper--nav-hover-white');
      return;
    }

    if (document.querySelector('.site-nav__expanded-item:hover, .site-nav__expanded-item:focus-within')) {
      return;
    }

    headerWrapper.classList.remove('header-wrapper--nav-hover-white');
  };

  const updatePaddleMenuTop = () => {
    const siteHeader = document.getElementById('SiteHeader');

    if (!siteHeader) {
      return;
    }

    document.documentElement.style.setProperty(
      '--paddle-nav-top',
      `${Math.max(0, siteHeader.getBoundingClientRect().bottom)}px`
    );
  };

  const getPaddleDetails = (target) => {
    return target?.closest?.('[data-paddle-nav-details]')
      || target?.closest?.('.site-nav__expanded-item')?.querySelector?.('[data-paddle-nav-details]')
      || null;
  };

  const openPaddleMenu = (details) => {
    if (!details) {
      return;
    }

    const item = details.closest('.site-nav__expanded-item');
    const summary = details.querySelector('[data-paddle-nav-trigger]');

    window.clearTimeout(details.paddleCloseTimer);
    updatePaddleMenuTop();

    item?.closest('.site-navigation')?.querySelectorAll('.site-nav__details[open]').forEach((openDetails) => {
      if (openDetails !== details) {
        openDetails.removeAttribute('open');
        openDetails.setAttribute('aria-expanded', 'false');
        openDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    });

    details.setAttribute('open', '');
    details.setAttribute('aria-expanded', 'true');
    summary?.setAttribute('aria-expanded', 'true');
    setNavHoverWhite(true);
  };

  const closePaddleMenu = (details) => {
    if (!details) {
      return;
    }

    window.clearTimeout(details.paddleCloseTimer);
    details.paddleCloseTimer = window.setTimeout(() => {
      const item = details.closest('.site-nav__expanded-item');

      if (item?.matches(':hover') || item?.matches(':focus-within')) {
        return;
      }

      details.removeAttribute('open');
      details.setAttribute('aria-expanded', 'false');
      details.querySelector('[data-paddle-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      setNavHoverWhite(false);
    }, 220);
  };

  const getExpandedNavItem = (target) => target?.closest?.('.site-nav__expanded-item') || null;

  document.addEventListener('mouseover', (event) => {
    if (getExpandedNavItem(event.target)) {
      setNavHoverWhite(true);
    }

    const details = getPaddleDetails(event.target);

    if (details) {
      openPaddleMenu(details);
    }
  });

  document.addEventListener('pointerover', (event) => {
    if (getExpandedNavItem(event.target)) {
      setNavHoverWhite(true);
    }

    const details = getPaddleDetails(event.target);

    if (details) {
      openPaddleMenu(details);
    }
  });

  document.addEventListener('focusin', (event) => {
    if (getExpandedNavItem(event.target)) {
      setNavHoverWhite(true);
    }

    const details = getPaddleDetails(event.target);

    if (details) {
      openPaddleMenu(details);
    }
  });

  document.addEventListener('pointerout', (event) => {
    const navItem = getExpandedNavItem(event.target);

    if (navItem && !navItem.contains(event.relatedTarget)) {
      window.setTimeout(() => setNavHoverWhite(false), 220);
    }

    const details = getPaddleDetails(event.target);

    if (details && !details.closest('.site-nav__expanded-item')?.contains(event.relatedTarget)) {
      closePaddleMenu(details);
    }
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-paddle-nav-trigger]');

    if (!trigger) {
      return;
    }

    event.preventDefault();
    openPaddleMenu(getPaddleDetails(trigger));
  });

  window.addEventListener('scroll', updatePaddleMenuTop, { passive: true });
  window.addEventListener('resize', updatePaddleMenuTop);
})();

// Mobile NavDrawer: when a top-level button opens a subnav, hide sibling rows.
(() => {
  if (window.navDrawerSubnavStateBound) {
    return;
  }

  window.navDrawerSubnavStateBound = true;

  const getNavDrawer = () => document.getElementById('NavDrawer');

  const resetSubnavState = () => {
    const drawer = getNavDrawer();
    const nav = drawer?.querySelector('.mobile-nav');

    if (!nav) {
      return;
    }

    drawer.classList.remove('is-subnav-active');
    nav.classList.remove('is-subnav-active');
    nav.querySelectorAll('.is-current-subnav').forEach((item) => {
      item.classList.remove('is-current-subnav');
    });
    nav.querySelectorAll('.mobile-nav__sublist.is-open').forEach((sublist) => {
      sublist.classList.remove('is-open');
      sublist.style.height = '';
    });
    nav.querySelectorAll('.mobile-nav__link--button.is-open, .collapsible-trigger.is-open').forEach((trigger) => {
      trigger.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    });
  };

  const setCurrentSubnav = (button) => {
    const drawer = getNavDrawer();
    const nav = drawer?.querySelector('.mobile-nav');
    const item = button?.closest('li.appear-animation');
    const sublist = document.getElementById(button?.getAttribute('aria-controls'));

    if (!nav || !item) {
      return;
    }

    nav.querySelectorAll('.is-current-subnav').forEach((navItem) => {
      if (navItem !== item) {
        navItem.classList.remove('is-current-subnav');
      }
    });

    drawer.classList.add('is-subnav-active');
    nav.classList.add('is-subnav-active');
    item.classList.add('is-current-subnav');

    drawer.querySelector('.drawer__scrollable')?.scrollTo(0, 0);
    sublist?.scrollTo(0, 0);
  };

  document.addEventListener('click', (event) => {
    const drawer = getNavDrawer();

    if (!drawer?.contains(event.target)) {
      return;
    }

    const backButton = event.target.closest('.paddle-mobile-nav__back');
    const closeButton = event.target.closest('.js-drawer-close');

    if (backButton || closeButton) {
      window.setTimeout(resetSubnavState, 0);
      window.setTimeout(resetSubnavState, 520);
      return;
    }

    const trigger = event.target.closest('.mobile-nav__link--button');

    if (!trigger || !drawer.contains(trigger)) {
      return;
    }

    window.setTimeout(() => {
      if (trigger.classList.contains('is-open')) {
        setCurrentSubnav(trigger);
        return;
      }

      resetSubnavState();
    }, 0);
  });

  document.addEventListener('drawerClose', resetSubnavState);
})();
